import { useState } from 'react';
import { Sparkles, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import { useTheme } from 'components/core/theme-provider';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { HttpError } from 'lib/https';
import { cn } from 'lib/utils';
import { learnedSkillErrorMessage } from './skills.service';
import { getSkillDisplayName, type LearnedSkillListItem } from './skills.types';
import { LearnedSkillDetailsModal } from './learned-skill-details-modal';
import { useDeleteLearnedSkill, useGetLearnedSkills } from './use-skills';

type LearnedSkillsPanelProps = {
  agentId: string;
  agentName: string;
};

export function LearnedSkillsPanel({ agentId, agentName }: LearnedSkillsPanelProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const skillsQuery = useGetLearnedSkills(agentId);
  const deleteSkill = useDeleteLearnedSkill(agentId);
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const [skillToDelete, setSkillToDelete] = useState<LearnedSkillListItem | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const titleColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const mutedColor = theme === 'dark' ? 'text-muted-foreground' : 'text-grey-500';
  const skills = skillsQuery.data?.pages.flatMap((page) => page.data) ?? [];

  const closeSkill = () => setSelectedSkillId(null);

  const handleDelete = async () => {
    if (!skillToDelete) return;
    try {
      await deleteSkill.mutateAsync(skillToDelete.id);
      if (selectedSkillId === skillToDelete.id) setSelectedSkillId(null);
      setSkillToDelete(null);
      setDeleteError('');
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        setSkillToDelete(null);
        void skillsQuery.refetch();
        return;
      }
      setDeleteError(
        learnedSkillErrorMessage(
          error,
          t('LEARNED_SKILL_DELETE_FAILED', { defaultValue: 'Could not delete this skill.' })
        )
      );
    }
  };

  return (
    <div className="flex min-h-0 flex-col gap-3">
      <p className={cn('text-xs font-medium leading-body', mutedColor)}>
        {agentName
          ? t('LEARNED_SKILLS_FOR_AGENT', {
              defaultValue: 'Skills {{agent}} learned in your conversations.',
              agent: agentName,
            })
          : t('LEARNED_SKILLS_HINT', {
              defaultValue: 'Skills this agent learned in your conversations.',
            })}
      </p>

      {skillsQuery.isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-16 rounded-lg" />
          <Skeleton className="h-16 rounded-lg" />
        </div>
      ) : null}

      {skillsQuery.isError ? (
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-destructive">
            {t('LEARNED_SKILLS_LOAD_FAILED', { defaultValue: 'Could not load learned skills.' })}
          </p>
          <Button
            type="button"
            variant="outline"
            className="h-10"
            onClick={() => void skillsQuery.refetch()}
          >
            {t('RETRY', { defaultValue: 'Retry' })}
          </Button>
        </div>
      ) : null}

      {!skillsQuery.isLoading && !skillsQuery.isError && skills.length === 0 ? (
        <div className="flex h-36 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-grey-100 bg-grey-50 px-4 text-center dark:border-border dark:bg-muted/30">
          <Sparkles className={cn('size-8', mutedColor)} strokeWidth={1.5} />
          <p className={cn('text-xs font-medium leading-body', mutedColor)}>
            {t('NO_LEARNED_SKILLS', { defaultValue: 'No learned skills yet.' })}
          </p>
        </div>
      ) : null}

      {skills.length > 0 ? (
        <div className="flex flex-col gap-2">
          {skills.map((skill) => {
            const name = getSkillDisplayName(skill);
            return (
              <div
                key={skill.id}
                className="flex min-w-0 items-center gap-2 rounded-lg border border-grey-100 px-3 py-3 dark:border-border"
              >
                <button
                  type="button"
                  onClick={() => setSelectedSkillId(skill.id)}
                  className="flex min-h-10 min-w-0 flex-1 flex-col items-start gap-1 text-left"
                >
                  <span className={cn('w-full truncate text-sm font-medium', titleColor)}>
                    {name}
                  </span>
                  {skill.description ? (
                    <span
                      className={cn('line-clamp-2 w-full text-xs leading-body', mutedColor)}
                      title={skill.description}
                    >
                      {skill.description}
                    </span>
                  ) : null}
                </button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 w-10 shrink-0 text-muted-foreground hover:text-destructive"
                  aria-label={t('DELETE_LEARNED_SKILL', {
                    defaultValue: 'Delete {{name}}',
                    name,
                  })}
                  onClick={() => {
                    setDeleteError('');
                    setSkillToDelete(skill);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      ) : null}

      {deleteError ? <p className="text-sm text-destructive">{deleteError}</p> : null}

      {skillsQuery.hasNextPage ? (
        <Button
          type="button"
          variant="outline"
          className="h-10 w-full"
          disabled={skillsQuery.isFetchingNextPage}
          onClick={() => void skillsQuery.fetchNextPage()}
        >
          {skillsQuery.isFetchingNextPage
            ? t('LOADING', { defaultValue: 'Loading...' })
            : t('LOAD_MORE', { defaultValue: 'Load more' })}
        </Button>
      ) : null}

      <LearnedSkillDetailsModal
        agentId={agentId}
        agentName={agentName}
        skillId={selectedSkillId}
        open={!!selectedSkillId}
        onClose={closeSkill}
        onUnavailable={() => {
          closeSkill();
          void skillsQuery.refetch();
        }}
      />

      <ConfirmationModal
        nested
        open={!!skillToDelete}
        onOpenChange={(open) => {
          if (!open) setSkillToDelete(null);
        }}
        title={t('LEARNED_SKILL_DELETE_TITLE', { defaultValue: 'Delete learned skill' })}
        description={t('LEARNED_SKILL_DELETE_DESCRIPTION', {
          defaultValue: 'Delete {{name}}? This permanently removes the skill. There is no undo.',
          name: skillToDelete ? getSkillDisplayName(skillToDelete) : '',
        })}
        onConfirm={handleDelete}
        confirmText={t('DELETE', { defaultValue: 'Delete' })}
        cancelText={t('CANCEL', { defaultValue: 'Cancel' })}
        confirmVariant="delete"
        preventAutoClose
        isLoading={deleteSkill.isPending}
      />
    </div>
  );
}
